import type { ChangeEvent } from 'react';

import classNames from 'classnames';

import styles from './RadioButton.module.css';

import { visuallyHidden } from '../../../styles';
import { Picture } from '../../Picture';
import { StatusMessage } from '../../StatusMessage';
import { Text } from '../../Text';
import { CircleMark } from '../CircleMark';
import type { RichSelectorOption } from '../RichSelector';

export type RadioButtonChangeEventHandler = (
  option: RichSelectorOption,
  e: ChangeEvent<HTMLInputElement>
) => void;

export interface RadioButtonProps {
  name: string;
  checked: boolean;
  option: RichSelectorOption;
  /** Toggle the description under the radio button. */
  showDescription?: boolean;
  showMobileImg: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onChange: RadioButtonChangeEventHandler;
  onMouseEnter: (option: RichSelectorOption) => void;
  onMouseLeave: () => void;
  onBlur: () => void;
  onFocus: (option: RichSelectorOption) => void;
}

/** Render the radio button with an image and extended description. */
export function RadioButton(props: RadioButtonProps) {
  return (
    <label
      onMouseEnter={() => props.onMouseEnter(props.option)}
      onMouseLeave={props.onMouseLeave}
    >
      <input
        name={props.name}
        type="radio"
        className={classNames(styles.input, visuallyHidden)}
        aria-label={props.option.label}
        onFocus={() => props.onFocus(props.option)}
        onBlur={props.onBlur}
        onChange={e => props.onChange(props.option, e)}
        checked={props.checked}
      />
      <div className={styles.container}>
        {/* Show small image on mobile screens when no option is selected. */}
        {props.showMobileImg && (
          <Picture
            className={classNames(styles.picture, styles.small)}
            sources={props.option.image.small}
            alt={props.option.imageAlt}
          />
        )}
        {/* Show large image on larger screens, regardless of an option being selected. */}
        <Picture
          className={classNames(styles.picture, styles.large)}
          sources={props.option.image.large}
          alt={props.option.imageAlt}
        />

        <CircleMark
          isFirst={props.isFirst}
          isLast={props.isLast}
          checked={props.checked}
        />
        <Text className={styles.label}>{props.option.label}</Text>
        <StatusMessage
          politeness="polite"
          visuallyHidden={!props.showDescription}
          className={styles.description}
        >
          <Text variant="heading-12" style={{ textAlign: 'center' }}>
            {props.option.description}
          </Text>
        </StatusMessage>
      </div>
    </label>
  );
}
